/**
 * Streets client connection using Server-Sent Events (SSE).
 * Replaces Socket.IO: server→client via EventSource, client→server via POST /streets/events.
 */

declare const process: {
  env: {
    REACT_APP_SERVER_URL: string;
  };
};

// Same host/port as API so SSE and device-screenshot share one process and connection map
const getServerBase = (): string => (process.env.REACT_APP_SERVER_URL || 'http://localhost:8080').replace(/\/$/, '');

type EventCallback = (data?: unknown) => void;

class StreetsSSEClient {
  private deviceId: string | null = null;

  private eventSource: EventSource | null = null;

  private listeners: Record<string, EventCallback[]> = {};

  private connectedState = false;

  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;

  get id(): string | null {
    return this.deviceId;
  }

  get connected(): boolean {
    return this.connectedState;
  }

  private dispatch(event: string, data: unknown): void {
    const cbs = this.listeners[event];
    if (cbs) cbs.forEach((cb) => cb(data));
  }

  on(event: string, callback: EventCallback): void {
    if (!this.listeners[event]) this.listeners[event] = [];
    this.listeners[event].push(callback);
  }

  off(event: string, callback?: EventCallback): void {
    if (!callback) {
      delete this.listeners[event];
      return;
    }
    const cbs = this.listeners[event];
    if (cbs) this.listeners[event] = cbs.filter((c) => c !== callback);
  }

  emit(event: string, data?: unknown): Promise<void> {
    if (!this.deviceId) return Promise.resolve();
    const base = getServerBase();
    return fetch(`${base}/streets/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ deviceId: this.deviceId, event, data }),
    })
      .then((res) => {
        if (!res.ok) console.error(`Events POST ${res.status}`);
      })
      .catch((err) => {
        console.warn('Streets events POST failed:', err);
        throw err;
      });
  }

  connect(deviceId: string): void {
    const id = (deviceId || '').trim();
    if (!id) return;
    const state = this.eventSource?.readyState;
    const alreadyConnected = this.deviceId === id && this.eventSource
      && (state === EventSource.OPEN || state === EventSource.CONNECTING);
    if (alreadyConnected) return;
    this.disconnect();
    this.deviceId = id;
    const base = getServerBase();
    const url = `${base}/streets/sse?deviceId=${encodeURIComponent(id)}`;
    const es = new EventSource(url);
    this.eventSource = es;

    es.onopen = (): void => {
      this.connectedState = true;
      this.dispatch('connect', undefined);
    };

    const handleEvent = (eventType: string, e?: MessageEvent): void => {
      let data: unknown;
      if (e?.data) {
        try {
          data = JSON.parse(e.data);
        } catch {
          data = e.data;
        }
      }
      this.dispatch(eventType, data);
    };

    es.addEventListener('refresh', ((e: Event) => handleEvent('refresh', e as MessageEvent)) as EventListener);
    es.addEventListener('screenshot', ((e: Event) => handleEvent('screenshot', e as MessageEvent)) as EventListener);
    es.addEventListener('show_uuid', ((e: Event) => handleEvent('show_uuid', e as MessageEvent)) as EventListener);
    es.addEventListener('debug', ((e: Event) => handleEvent('debug', e as MessageEvent)) as EventListener);
    es.addEventListener('streetsAuth', ((e: Event) => handleEvent('streetsAuth', e as MessageEvent)) as EventListener);

    es.onerror = (): void => {
      this.connectedState = false;
      this.dispatch('disconnect', 'connection error');
      es.close();
      this.eventSource = null;
      this.reconnectTimeout = setTimeout(() => this.deviceId && this.connect(this.deviceId), 2000);
    };
  }

  disconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    this.connectedState = false;
  }
}

export const socket = new StreetsSSEClient();
export default socket;
